import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { BiCameraOff, BiCheck, BiLoader, BiPause, BiPlay, BiStop, BiTrash, BiX } from 'react-icons/bi';
import WebcamVideo from 'react-webcam';

import { PSInput } from '@/components/ui/input';

type Props = {
  onSubmit: (file: File, url: string) => void;
  onClose?: () => void;
};

type State = {
  capturing: boolean;
  recordedChunks: Blob[];
  url: string | null;
  isVideoPaused: boolean;
  recordTimer: number;
  file: File | null;
  audio: boolean;
  isLoading: boolean;
  errorMessage?: string | null;
};

type Action =
  | { type: 'START_CAPTURE' }
  | { type: 'STOP_CAPTURE' }
  | { type: 'SET_URL'; payload: string | null }
  | { type: 'SET_FILE'; payload: File | null }
  | { type: 'SET_IS_VIDEO_PAUSED'; payload: boolean }
  | { type: 'SET_RECORD_TIMER'; payload: number }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'ADD_RECORDED_CHUNK'; payload: Blob }
  | { type: 'RESET_RECORDED_CHUNKS' };

const initialState: State = {
  capturing: false,
  recordedChunks: [],
  url: null,
  isVideoPaused: false,
  recordTimer: 0,
  file: null,
  audio: true,
  isLoading: false,
  errorMessage: null,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'START_CAPTURE':
      return {
        ...state,
        capturing: true,
        url: null,
        file: null,
        isVideoPaused: false,
        recordedChunks: [],
        recordTimer: 0,
      };
    case 'STOP_CAPTURE':
      return { ...state, capturing: false };
    case 'SET_URL':
      return { ...state, url: action.payload };
    case 'SET_FILE':
      return { ...state, file: action.payload };
    case 'SET_IS_VIDEO_PAUSED':
      return { ...state, isVideoPaused: action.payload };
    case 'SET_RECORD_TIMER':
      return { ...state, recordTimer: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, errorMessage: action.payload };
    case 'ADD_RECORDED_CHUNK':
      return {
        ...state,
        recordedChunks: [...state.recordedChunks, action.payload],
      };
    case 'RESET_RECORDED_CHUNKS':
      return {
        ...state,
        recordedChunks: [],
        url: null,
        file: null,
        isVideoPaused: false,
      };
    default:
      return state;
  }
};

const VideoCameraComponent: React.FC<Props> = ({ onSubmit, onClose }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const webcamRef = useRef<any>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<any>(null);

  const videoConstraints = {
    aspectRatio: 0.6666666667,
    facingMode: 'user',
  };

  useEffect(() => {
    dispatch({ type: 'SET_LOADING', payload: true });

    setTimeout(() => {
      dispatch({ type: 'SET_LOADING', payload: false });

      if (webcamRef.current.video) {
        webcamRef.current.video.muted = true;
      }
    }, 2000);
  }, []);

  useEffect(() => {
    // Declare a variable to hold the interval timer
    let intervalTimer: NodeJS.Timeout | null = null;

    if (state.capturing) {
      // If capturing, set an interval to increment the record timer every second
      intervalTimer = setInterval(() => {
        dispatch({ type: 'SET_RECORD_TIMER', payload: state.recordTimer + 1 });
      }, 1000);
    } else if (intervalTimer) {
      // If not capturing and there is an interval timer, clear the interval
      clearInterval(intervalTimer);
    }

    // Cleanup function to clear the interval when the component unmounts or state.capturing changes
    return () => {
      if (intervalTimer) {
        clearInterval(intervalTimer);
      }
    };
  }, [state.capturing, state.recordTimer]);

  // if there is not url then video pause
  useEffect(() => {
    if (state.url == null) {
      videoRef?.current?.pause();
    }
  }, [state.url]);

  const handleStartCaptureClick = useCallback(() => {
    dispatch({ type: 'START_CAPTURE' });
    const mimeType = getSupportedMimeType();
    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
      mimeType: mimeType.format,
    });
    mediaRecorderRef.current.addEventListener('dataavailable', (event: any) => {
      if (event.data.size > 0) {
        dispatch({ type: 'ADD_RECORDED_CHUNK', payload: event.data });
      }
    });
    mediaRecorderRef.current.start();
  }, [webcamRef, dispatch]);

  const handleStopCaptureClick = useCallback(() => {
    mediaRecorderRef.current.stop();
    dispatch({ type: 'STOP_CAPTURE' });
  }, [mediaRecorderRef, dispatch]);

  const handleTogglePlayClick = useCallback(() => {
    if (videoRef.current?.paused) {
      videoRef.current.play();
      dispatch({ type: 'SET_IS_VIDEO_PAUSED', payload: true });
    } else {
      videoRef?.current?.pause();
      dispatch({ type: 'SET_IS_VIDEO_PAUSED', payload: false });
    }
  }, [videoRef, dispatch]);

  useEffect(() => {
    const mimeType = getSupportedMimeType();
    if (state.recordedChunks.length) {
      const blob = new Blob(state.recordedChunks, {
        type: mimeType.format,
      });
      const url = URL.createObjectURL(blob);
      dispatch({
        type: 'SET_FILE',
        payload: new File([blob], `${Date.now()}${mimeType.extension}`, {
          type: mimeType.format,
        }),
      });
      dispatch({ type: 'SET_URL', payload: url });
    }
  }, [state.recordedChunks]);

  const getSupportedMimeType = () => {
    const types = ['video/webm', 'video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=h264,opus', 'video/mp4'];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return {
          format: type,
          extension: type.includes('mp4') ? '.mp4' : '.webm',
        };
      }
    }

    return {
      format: 'video/mp4',
      extension: '.mp4',
    };
  };

  return (
    <Modal centered show={true} className="modal-dialog-centered modal-fullscreen tw-bg-black " contentClassName="tw-flex tw-items-center">
      <div className="tw-w-full tw-relative">
        <WebcamVideo
          className={state.url || state.errorMessage != null || state.isLoading ? 'tw-hidden' : 'tw-flex tw-max-h-screen tw-object-cover'}
          ref={webcamRef}
          width={'100%'}
          height={'100%'}
          videoConstraints={videoConstraints}
          audio={true}
          onUserMediaError={(error: any) => {
            const strError = String(error);
            if (strError.includes('Permission')) {
              dispatch({ type: 'SET_ERROR', payload: 'Permission denied' });
            } else {
              dispatch({ type: 'SET_ERROR', payload: strError });
            }
          }}
        />
        <video
          preload="auto"
          key={state.url}
          className={state.url && state.isLoading == false ? 'tw-flex tw-max-h-screen tw-object-cover' : 'tw-hidden'}
          ref={videoRef}
          src={state.url || ''}
          width={'100%'}
          height={'100%'}
          onEnded={() => dispatch({ type: 'SET_IS_VIDEO_PAUSED', payload: false })}
        >
          <track kind="captions" />
        </video>

        {state.errorMessage != null && state.isLoading == false && <VideoErorr message={state.errorMessage} />}

        {state.isLoading && <VideoLoading />}

        <div
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') onClose();
          }}
          className="tw-absolute tw-top-1 tw-right-2 tw-w-10 tw-h-10 p-2 tw-bg-gray-800 tw-rounded-[99px] tw-justify-start tw-items-start tw-gap-2.5 tw-inline-flex"
        >
          <div className="tw-cursor-pointer tw-w-6 tw-h-6 relative tw-flex tw-items-center tw-justify-center">
            <BiX className="tw-text-white tw-text-2xl" />
          </div>
        </div>
        {state.errorMessage == null && (
          <div className="tw-flex tw-flex-col tw-gap-2 tw-absolute tw-bottom-6 tw-left-[50%] tw-translate-x-[-50%] tw-w-[90%] tw-p-4 tw-bg-gray-900 tw-rounded-2xl">
            {state.url && <VideoProgressBar videoRef={videoRef} recordTime={state.recordTimer} />}
            <div className="tw-justify-between tw-items-center tw-inline-flex tw-w-full">
              <div className="tw-justify-start tw-items-start tw-gap-3 tw-flex">
                {state.recordedChunks.length > 0 && !state.capturing && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => dispatch({ type: 'RESET_RECORDED_CHUNKS' })}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') dispatch({ type: 'RESET_RECORDED_CHUNKS' });
                    }}
                    className="tw-cursor-pointer tw-p-2 tw-bg-gray-800 tw-rounded-[99px] tw-justify-start tw-items-start tw-gap-2.5 tw-flex"
                  >
                    <div className="tw-w-6 tw-h-6 tw-relative tw-flex tw-items-center tw-justify-center">
                      <BiTrash className="tw-text-white tw-text-2xl" />
                    </div>
                  </div>
                )}
              </div>
              <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-3 tw-flex">
                {state.capturing ? (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={handleStopCaptureClick}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') handleStopCaptureClick();
                    }}
                    className="tw-cursor-pointer tw-p-2 tw-bg-gray-800 tw-rounded-[99px] tw-justify-start tw-items-start tw-gap-2.5 tw-flex"
                  >
                    <div className="tw-w-6 tw-h-6 tw-relative tw-flex tw-items-center tw-justify-center">
                      <BiStop className="tw-text-white tw-text-2xl" />
                    </div>
                  </div>
                ) : (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={handleStartCaptureClick}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') handleStartCaptureClick();
                    }}
                    className="tw-cursor-pointer tw-p-2 tw-bg-gray-800 tw-rounded-[99px] tw-justify-start tw-items-start tw-gap-2.5 tw-flex"
                  >
                    <div className="tw-p-1.5 tw-justify-center tw-items-center tw-gap-2.5 tw-flex">
                      <div className="tw-w-3 tw-h-3 tw-bg-red-500 tw-rounded-full" />
                    </div>
                  </div>
                )}
                {state.url && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={handleTogglePlayClick}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') handleTogglePlayClick();
                    }}
                    className="tw-cursor-pointer tw-p-2 tw-bg-gray-800 tw-rounded-[99px] tw-justify-start tw-items-start tw-gap-2.5 tw-flex"
                  >
                    <div className="tw-w-6 tw-h-6 tw-relative tw-flex tw-items-center tw-justify-center">
                      {state.isVideoPaused ? <BiPause className="tw-text-white tw-text-2xl" /> : <BiPlay className="tw-text-white tw-text-2xl" />}
                    </div>
                  </div>
                )}
              </div>
              <div className="tw-justify-start tw-items-start tw-gap-3 tw-flex">
                {state.file && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => onSubmit(state.file as File, state.url as string)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') onSubmit(state.file as File, state.url as string);
                    }}
                    className="tw-cursor-pointer tw-p-2 tw-bg-primary tw-rounded-[99px] tw-justify-start tw-items-start tw-gap-2.5 tw-flex"
                  >
                    <div className="tw-w-6 tw-h-6 tw-relative tw-flex tw-items-center tw-justify-center">
                      <BiCheck className="tw-text-white tw-text-2xl" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default VideoCameraComponent;

type VideoProgressBarProps = {
  videoRef: React.RefObject<HTMLVideoElement>;
  recordTime: number;
};

const VideoProgressBar: React.FC<VideoProgressBarProps> = ({ videoRef, recordTime }) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [videoTime, setVideoTime] = useState(0);

  const getProgress = () => {
    if (videoRef?.current && videoRef.current.currentTime !== 0 && videoRef.current.duration !== 0) {
      let durationTemp = videoRef.current.duration;

      if (durationTemp === Infinity) {
        durationTemp = recordTime;
      }

      const progressTemp = ((videoRef.current.currentTime as number) / durationTemp) * 100;

      return progressTemp === Infinity ? 0 : ~~progressTemp;
    }
    return 0;
  };

  useEffect(() => {
    if (videoRef) {
      const interval = setInterval(() => {
        if (videoRef.current) {
          setCurrentTime(videoRef.current.currentTime);
          setProgress(getProgress());
        }
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [videoRef, currentTime]);

  useEffect(() => {
    if (videoRef.current?.duration && videoRef.current.duration !== Infinity) {
      setVideoTime(videoRef.current.duration);
    } else if (recordTime) {
      setVideoTime(recordTime);
    }
  }, [videoRef, recordTime]);

  const handleVideoProgress = (event: React.ChangeEvent<HTMLInputElement>) => {
    const manualChange = Number(event.target.value);

    if (videoRef.current?.currentTime && videoRef.current.duration) {
      videoRef.current.currentTime = (videoRef.current.duration / 100) * manualChange;
    }
    setProgress(manualChange);
  };

  return (
    <div className="tw-w-full tw-h-[30px] tw-relative">
      <PSInput
        value={progress}
        className="tw-w-full"
        onChange={handleVideoProgress}
        style={{
          background: `linear-gradient(to right, #1993fb ${progress}%, #ccc ${progress}%)`,
        }}
        id="range"
        type="range"
        min="0"
        max="100"
      />
      <div className="tw-left-0 tw-top-[18px] tw-absolute tw-text-white tw-text-xs tw-font-normal">
        {Math.floor(currentTime / 60) + ':' + ('0' + Math.floor(currentTime % 60)).slice(-2)}
      </div>
      <div className="tw-left-[400px] tw-top-[18px] tw-absolute tw-text-right tw-text-white tw-text-xs tw-font-normal">
        {Math.floor(videoTime / 60) + ':' + ('0' + Math.floor(videoTime % 60)).slice(-2)}
      </div>
    </div>
  );
};

const VideoErorr = ({ message }: { message: string }) => {
  return (
    <div className="tw-bg-black tw-w-full tw-h-[400px] tw-flex tw-flex-col tw-justify-center tw-items-center">
      <BiCameraOff className="tw-text-white tw-text-9xl" />
      <p className="tw-text-white tw-text-3xl">{message}</p>
    </div>
  );
};

const VideoLoading = () => {
  return (
    <div className="tw-bg-black tw-w-full tw-h-[400px] tw-flex tw-flex-col tw-justify-center tw-items-center">
      <BiLoader className="tw-text-white tw-text-9xl tw-animate-spin" />
      <p className="tw-text-white tw-text-3xl">Loading ...</p>
    </div>
  );
};
